import {dbOperation} from './database';

// Case study storage utilities using the database layer
export const saveCaseStudy=async (caseStudy)=> {
  try {
    const studyData={
      ...caseStudy,
      created_at: caseStudy.createdAt || new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    console.log('Saving case study to database:',studyData);
    const result=await dbOperation.saveCaseStudy(studyData);
    console.log('Case study save result:',result);
    
    return result;
  } catch (error) {
    console.error('Error in saveCaseStudy:',error);
    throw error;
  }
};

export const getAllCaseStudies=async ()=> {
  try {
    console.log('Getting all case studies...');
    const studies=await dbOperation.getAllCaseStudies();
    console.log('Retrieved case studies:',studies);
    
    const sortedStudies=studies.sort((a,b)=> 
      new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0)
    );
    
    return sortedStudies;
  } catch (error) {
    console.error('Error in getAllCaseStudies:',error);
    return [];
  }
};

export const getCaseStudy=async (id)=> {
  try {
    console.log('Getting case study by id:',id);
    const studies=await getAllCaseStudies();
    const study=studies.find(study=> study.id===id) || null;
    console.log('Found case study:',study);
    return study;
  } catch (error) {
    console.error('Error in getCaseStudy:',error);
    return null;
  }
};

export const deleteCaseStudy=async (id)=> {
  try {
    console.log('Deleting case study with id:',id);
    const result=await dbOperation.deleteCaseStudy(id);
    console.log('Delete case study result:',result);
    
    if (result.success) {
      console.log('✅ Case study deleted successfully');
    } else {
      console.error('❌ Failed to delete case study:',result.error);
    }
    
    return result;
  } catch (error) {
    console.error('Error in deleteCaseStudy:',error);
    throw error;
  }
};

export const getActiveCaseStudies=async ()=> {
  try {
    const studies=await getAllCaseStudies();
    return studies.filter(study=> study.status==='active');
  } catch (error) {
    console.error('Error getting active case studies:',error);
    return [];
  }
};

export const getFeaturedCaseStudies=async ()=> {
  try {
    const studies=await getActiveCaseStudies();
    return studies.filter(study=> study.featured);
  } catch (error) {
    console.error('Error getting featured case studies:',error);
    return [];
  }
};

export const getCaseStudiesByIndustry=async (industry)=> {
  try {
    const studies=await getActiveCaseStudies();
    return studies.filter(study=> study.industry===industry);
  } catch (error) {
    console.error('Error getting case studies by industry:',error);
    return [];
  }
};

export const searchCaseStudies=async (query)=> {
  try {
    const studies=await getActiveCaseStudies();
    const lowercaseQuery=query.toLowerCase();
    
    return studies.filter(study=> 
      study.title.toLowerCase().includes(lowercaseQuery) ||
      study.client.toLowerCase().includes(lowercaseQuery) ||
      study.challenge.toLowerCase().includes(lowercaseQuery) ||
      study.solution.toLowerCase().includes(lowercaseQuery) ||
      (study.technologies && study.technologies.some(tech=> 
        tech.toLowerCase().includes(lowercaseQuery)
      ))
    );
  } catch (error) {
    console.error('Error searching case studies:',error);
    return [];
  }
};

// Initialize with sample case studies if none exist
export const initializeSampleCaseStudies=async ()=> {
  try {
    const existingStudies=await getAllCaseStudies();
    
    if (existingStudies.length===0) {
      console.log('Initializing sample case studies...');
      
      const sampleStudies=[
        {
          id: '1',
          title: 'FinTech Mobile Banking Revolution',
          client: 'NextGen Banking',
          industry: 'FinTech',
          projectType: 'Mobile App',
          challenge: 'Slow time-to-market for a new mobile banking app with complex security requirements and real-time transaction processing.',
          solution: 'Leveraged AI for automated code generation and intelligent testing frameworks, implementing advanced security protocols with AI-assisted vulnerability detection.',
          results: [
            {metric: 'Development time reduced', value: '65%'},
            {metric: 'Cost savings achieved', value: '40%'},
            {metric: 'Security vulnerabilities prevented', value: '100%'},
            {metric: 'Launch timeline', value: '3 months ahead'}
          ],
          testimonial: 'aiximo.com transformed our development process. What would have taken 18 months was delivered in 6 months with superior quality and security.',
          author: 'Sarah Chen, CTO at NextGen Banking',
          image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
          gallery: [
            'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop'
          ],
          projectUrl: 'https://nextgen-banking.com',
          demoUrl: 'https://demo.nextgen-banking.com',
          technologies: ['React Native', 'Node.js', 'PostgreSQL', 'AI/ML', 'AWS'],
          duration: '6 months',
          teamSize: '5 developers',
          featured: true,
          status: 'active',
          createdAt: '2024-09-01T10:00:00.000Z',
          updatedAt: '2024-09-01T10:00:00.000Z'
        },
        {
          id: '2',
          title: 'E-commerce Platform Optimization',
          client: 'ShopSmart Solutions',
          industry: 'E-commerce',
          projectType: 'Web Application',
          challenge: 'High development costs for a complex data analytics platform with real-time inventory management and personalized recommendations.',
          solution: 'Utilized prompt engineering to rapidly develop core functionalities and AI-powered analytics engine for personalized user experiences.',
          results: [
            {metric: 'Development costs reduced', value: '55%'},
            {metric: 'Performance improvement', value: '80%'},
            {metric: 'User engagement increased', value: '120%'},
            {metric: 'Revenue growth', value: '45%'}
          ],
          testimonial: 'The AI-driven approach exceeded our expectations. Our platform now processes millions of transactions seamlessly with intelligent recommendations.',
          author: 'Michael Rodriguez, Founder of ShopSmart',
          image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop',
          gallery: [],
          projectUrl: 'https://shopsmart-solutions.com',
          demoUrl: '',
          technologies: ['React', 'Next.js', 'MongoDB', 'Redis', 'Stripe'],
          duration: '4 months',
          teamSize: '6 developers',
          featured: false,
          status: 'active',
          createdAt: '2024-08-15T10:00:00.000Z',
          updatedAt: '2024-08-15T10:00:00.000Z'
        }
      ];

      for (const study of sampleStudies) {
        await saveCaseStudy(study);
        console.log(`✅ Created sample case study: ${study.title}`);
      }
      
      console.log(`✅ Initialized ${sampleStudies.length} sample case studies`);
      return true;
    }
    
    console.log(`ℹ️ Found ${existingStudies.length} existing case studies`);
    return false;
  } catch (error) {
    console.error('Error initializing sample case studies:', error);
    return false;
  }
};